import { useEffect, useState, useRef, useCallback } from "react";

interface CountdownProps {
  duration: number;
  onTimeEnd?: () => void;
  isActive?: boolean;
}

const useCountdown = ({ duration, onTimeEnd, isActive = true }: CountdownProps) => {
  const [remainingTime, setRemainingTime] = useState(duration);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const startTimer = useCallback(() => {
    clearTimer();
    intervalRef.current = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 1) {
          clearTimer();
          if (onTimeEnd) {
            onTimeEnd();
          }

          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  }, [onTimeEnd]);

  useEffect(() => {
    if (isActive) {
      startTimer();
    }

    return () => {
      clearTimer();
    };
  }, [startTimer, isActive]);

  const restart = () => {
    setRemainingTime(duration);
    if (isActive) {
      startTimer();
    }
  };

  const days = Math.floor(remainingTime / (60 * 60 * 24));
  const hours = Math.floor((remainingTime % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((remainingTime % (60 * 60)) / 60);
  const seconds = remainingTime % 60;

  return {
    days,
    hours,
    minutes,
    seconds,
    duration,
    restart,
  };
};

export default useCountdown;
